import '@georapbox/a-tab-group/dist/a-tab-group.js';
import { upgradeProperty } from '../../../../utils/upgradeProperty/upgradeProperty.js';
import { checkIfAnalyticsIsDisabled } from '../../../../utils/isAnalyticsDisabled/isAnalyticsDisabled.js';
import { isPlainObject } from '../../../../utils/isPlainObject/isPlainObject.js';
import { Analytics } from '../../../../services/analytics.js';
import { loadClient } from '@cx-modules/offers-grouped';
import { cxModuleLoader, render } from '../../../../services/cx-module-helper';
import { createCxProps } from '../../utils.js';
import { html } from '../../../../utils/lit/html.js';
import createI18nInstance from '../../../../services/i18n/index.js';

const WIDGET_NAME = 'oc-offers-widget-variation-grouped-offers';
const DEFAULT_VISIBLE_OFFERS_COUNT = 3;
const template = document.createElement('template');
template.innerHTML = html`
  <style>
    :host {
      all: initial;
      display: block;
      width: 100%;
      font-family: inherit;
      box-sizing: border-box;
    }
  </style>
`;

const wEvent = (name, detail) =>
  new CustomEvent(`${WIDGET_NAME}:${name}`, {
    bubbles: true,
    composed: true,
    detail
  });

class OCOffersWidgetVariationGroupedOffers extends HTMLElement {
  #theme;
  #initialData = null;
  #state = { data: null };
  #isAnalyticsDisabled;

  constructor() {
    super();

    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  static get observedAttributes() {
    return ['placeholders'];
  }

  get _initialData() {
    return this.#initialData;
  }

  // NOTE `_initialData` should not be used manually.
  // Its purpose is to be used by the host element to provide the initial data to the child component.
  set _initialData(value) {
    if (isPlainObject(value)) {
      this.#initialData = value;
    }
  }

  async connectedCallback() {
    upgradeProperty(this, '_initialData');

    this.#theme = this.getAttribute('theme') || 'default';

    const data = this._initialData;

    this.#state.data = { ...data };

    if (!data || !Array.isArray(data.offers) || !data.offers.length) {
      return;
    }

    this.#isAnalyticsDisabled = checkIfAnalyticsIsDisabled(
      data.trackingRate,
      this.hasAttribute('disable-analytics')
    );

    if (!this.#isAnalyticsDisabled) {
      this.analytics = new Analytics(
        data.gtmId,
        data.useDefaultDataLayer ? 'dataLayer' : undefined
      );
      this.analytics.integrateAnalytics();
    }

    if ('IntersectionObserver' in window) {
      const intersectionObserver = new IntersectionObserver(entries => {
        const entry = entries[0];
        const {
          recommendedMarketId,
          hash: widgetSlot,
          campaignName
        } = data || {};

        if (entry?.isIntersecting) {
          this.#trackEvent({
            variation: 'grouped-offers',
            event: 'widget_impression',
            category: 'Widget',
            action: 'impression',
            label: 'widget_impression',
            page: window.location.href,
            widgetType: 'offers',
            ...(widgetSlot &&
              campaignName && {
                widgetSlot,
                campaignName
              }),
            ...(recommendedMarketId ? { recommendedMarketId } : {})
          });

          intersectionObserver.disconnect();
        }
      });

      intersectionObserver.observe(this);
    }

    try {
      const target = document.createElement('div');
      this.shadowRoot.appendChild(target);

      const { Component } = await cxModuleLoader(
        loadClient,
        this.shadowRoot,
        {}
      );

      if (!Component || !target) {
        return;
      }

      const i18n = data?.repub ? await createI18nInstance(data.repub) : null;

      const cxProps = createCxProps({
        data,
        variant: 'offers-grouped',
        theme: this.#theme ?? data.theme,
        analytics: {
          ...(!this.#isAnalyticsDisabled && {
            dataLayer: this.analytics?.getDataLayer()
          }),
          disableAnalytics: this.#isAnalyticsDisabled
        },
        initialOffersToShow:
          data.initialOffersToShow ?? this.#getVisibleOffersCount(),
        placeholders: this.getAttribute('placeholders'),
        ...(i18n && { i18n })
      });

      await render(target, Component, cxProps);
      this.dispatchEvent(wEvent('load-success', data));
    } catch (error) {
      this.dispatchEvent(wEvent('load-error', { error }));
    } finally {
      this.dispatchEvent(wEvent('load'));
    }
  }

  disconnectedCallback() {}

  #getVisibleOffersCount() {
    return (
      Math.abs(parseInt(this.getAttribute('visible-offers-count'), 10)) ||
      DEFAULT_VISIBLE_OFFERS_COUNT
    );
  }

  #trackEvent(event) {
    if (this.#isAnalyticsDisabled) {
      return;
    }

    this.analytics.trackEvent(event);
  }
}

if (window.customElements && !window.customElements.get(WIDGET_NAME)) {
  window.customElements.define(
    WIDGET_NAME,
    OCOffersWidgetVariationGroupedOffers
  );
}
